import { useMemo } from "react";

import { ReviewPlanPendingArea } from "../components/ReviewPlanVisitsList";

type AreaGroup = {
    plannerArea: ReviewPlanPendingArea["plannerArea"];
    visits: ReviewPlanPendingArea["plannerVisits"];
};

export type UserGroup = {
    userId: string | null;
    userName: string;
    stagedByUser: ReviewPlanPendingArea["plannerVisits"][number]["stagedByUser"];
    isCurrentUser: boolean;
    toBeCancelled: AreaGroup[];
    toBePlanned: AreaGroup[];
    visitIds: string[];
};

export function useReviewPlanVisitGrouping(
    areasToBeCancelled: ReviewPlanPendingArea[],
    areasToBePlanned: ReviewPlanPendingArea[],
    currentUserId: string | undefined,
): UserGroup[] {
    return useMemo(() => {
        const groupsMap = new Map<string | null, UserGroup>();

        const addVisits = (areas: ReviewPlanPendingArea[], bucket: "toBeCancelled" | "toBePlanned") => {
            for (const area of areas) {
                for (const visit of area.plannerVisits) {
                    const userId = visit.stagedByUser.data?.id ?? null;
                    const userName = visit.stagedByUser.data ? `${visit.stagedByUser.data.firstName} ${visit.stagedByUser.data.lastName}` : null;

                    if (!groupsMap.has(userId)) {
                        groupsMap.set(userId, {
                            userId,
                            userName: userName ?? "",
                            stagedByUser: visit.stagedByUser ?? null,
                            isCurrentUser: currentUserId !== undefined && userId === currentUserId,
                            toBeCancelled: [],
                            toBePlanned: [],
                            visitIds: [],
                        });
                    }

                    const group = groupsMap.get(userId)!;
                    const existingArea = group[bucket].find(a => a.plannerArea.id === area.plannerArea.id);
                    if (existingArea) {
                        existingArea.visits.push(visit);
                    } else {
                        group[bucket].push({
                            plannerArea: area.plannerArea,
                            visits: [visit],
                        });
                    }
                    group.visitIds.push(visit.id);
                }
            }
        };

        addVisits(areasToBeCancelled, "toBeCancelled");
        addVisits(areasToBePlanned, "toBePlanned");

        const groups = Array.from(groupsMap.values());
        groups.sort((a, b) => {
            if (a.isCurrentUser) return -1;
            if (b.isCurrentUser) return 1;
            if (a.userId === null) return 1;
            if (b.userId === null) return -1;
            return a.userName.localeCompare(b.userName);
        });

        return groups;
    }, [areasToBeCancelled, areasToBePlanned, currentUserId]);
}
